/** Rubrique Utilisateurs et rôles : instance d'application, liste, invitation, rôle, désactivation.
 * Gérant : gère tous les comptes + instance + invitation.
 * Sommelier / Serveur : ne voient et n'éditent que leur propre compte (nom + mot de passe). */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Pencil, Mail, Trash2, Server, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { parametresApi, authApi } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { Button, Spinner, Card, Tag } from '../../../components/ui/kit';

const ROLES = { gerant: 'Gérant', sommelier: 'Sommelier', serveur: 'Serveur' };

function CopyField({ label, value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <div>
      <span className="text-[12px] text-n600 block mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate vd-input h-9 flex items-center bg-surface text-[13px]" data-testid={testid}>{value || '—'}</code>
        <button className="vd-btn vd-btn-ghost h-9 px-2" onClick={() => { navigator.clipboard.writeText(value || ''); setDone(true); toast.success('Copié'); setTimeout(() => setDone(false), 1500); }} data-testid={`${testid}-copy`} disabled={!value}>
          {done ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function InstanceBlock() {
  const { data } = useQuery({ queryKey: ['app-instance'], queryFn: parametresApi.appInstance });
  const appUrl = data?.app_url || window.location.origin;
  return (
    <Card className="p-4 mb-5" data-testid="instance-block">
      <div className="flex items-center gap-2 mb-3"><Server className="w-4 h-4 text-gold700" /><p className="font-title text-lg">Instance d'application</p></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <CopyField label="URL d'accès à l'application" value={appUrl} testid="instance-url" />
        <CopyField label="ID d'instance" value={data?.instance_id} testid="instance-id" />
      </div>
    </Card>
  );
}

/** Vue auto-gestion : sommelier / serveur ne voient que leur propre compte. */
function SelfProfile() {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  if (!user) return <Spinner />;
  return (
    <div className="max-w-[900px]">
      <h2 className="font-title text-3xl mb-1">Mon compte</h2>
      <p className="text-[13px] text-n600 mb-5">Vous pouvez modifier votre nom et votre mot de passe.</p>
      <div className="vd-card overflow-hidden">
        <table className="w-full text-[13.5px]" data-testid="users-table">
          <thead><tr className="text-left kicker"><th className="py-2.5 px-3">Nom</th><th className="py-2.5 px-3">Email</th><th className="py-2.5 px-3">Rôle</th><th className="py-2.5 px-3 text-right">Actions</th></tr></thead>
          <tbody>
            <tr className="hair-t" data-testid={`user-${user.id}`}>
              <td className="py-2.5 px-3">{user.name}</td>
              <td className="py-2.5 px-3 text-n600">{user.email}</td>
              <td className="py-2.5 px-3"><Tag tone="neutral">{ROLES[user.role] || user.role}</Tag></td>
              <td className="py-2.5 px-3">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => setEditing(true)} title="Modifier mon profil" className="p-1.5 text-n600 hover:text-ink" data-testid={`user-edit-${user.id}`}><Pencil className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {editing && <SelfEditModal user={user} onClose={() => setEditing(false)}
        onSaved={async () => { await refreshUser(); setEditing(false); }} />}
    </div>
  );
}

function SelfEditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || '', password: '', password2: '', current: '' });
  const save = useMutation({
    mutationFn: (patch) => authApi.updateProfile(patch),
    onSuccess: async () => { toast.success(form.password.trim() ? 'Profil mis à jour — email de notification envoyé' : 'Profil mis à jour'); await onSaved(); },
    onError: (e) => toast.error(e.message || 'Échec de la mise à jour'),
  });
  const submit = () => {
    const pw = form.password.trim();
    const name = form.name.trim();
    if (pw) {
      if (pw !== form.password2.trim()) { toast.error('Les deux mots de passe ne correspondent pas'); return; }
      if (pw.length < 6) { toast.error('Mot de passe trop court (6 caractères minimum)'); return; }
      if (!form.current.trim()) { toast.error('Saisissez votre mot de passe actuel'); return; }
    }
    const patch = {};
    if (name && name !== user.name) patch.name = name;
    if (pw) { patch.password = pw; patch.current_password = form.current.trim(); }
    if (!patch.name && !patch.password) { toast.error('Aucune modification à enregistrer'); return; }
    save.mutate(patch);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div className="bg-paper border border-hair rounded-lg shadow-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="user-edit-modal">
        <h3 className="font-title text-2xl mb-4">Modifier mon profil</h3>
        <div className="space-y-3">
          <label className="block"><span className="text-[12px] text-n600">Nom</span>
            <input className="vd-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-edit-name" /></label>
          <label className="block"><span className="text-[12px] text-n600">Email</span>
            <input className="vd-input bg-surface text-n600" value={user.email} disabled data-testid="user-edit-email" /></label>
          <label className="block"><span className="text-[12px] text-n600">Nouveau mot de passe (laisser vide pour ne pas changer)</span>
            <input type="password" className="vd-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••" data-testid="user-edit-password" /></label>
          {form.password.trim() && (
            <>
              <label className="block"><span className="text-[12px] text-n600">Vérifier le nouveau mot de passe</span>
                <input type="password" className="vd-input" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} placeholder="••••••" data-testid="user-edit-password2" /></label>
              <label className="block"><span className="text-[12px] text-n600">Mot de passe actuel</span>
                <input type="password" className="vd-input" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} placeholder="••••••" data-testid="user-edit-current" /></label>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" disabled={save.isPending} onClick={submit} data-testid="user-edit-save">Enregistrer</Button>
        </div>
      </div>
    </div>
  );
}

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', email: '', role: 'serveur' });
  const [editing, setEditing] = useState(null);
  const isGerant = me?.role === 'gerant';

  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: parametresApi.users, enabled: isGerant });

  const invite = useMutation({
    mutationFn: () => parametresApi.inviteUser(form),
    onSuccess: (d) => {
      toast.success(d.email_sent ? 'Invitation envoyée par email' : `Créé — mot de passe temporaire : ${d.temp_password}`);
      setForm({ name: '', email: '', role: 'serveur' });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => toast.error('Échec (email déjà utilisé ?)'),
  });
  const upd = useMutation({ mutationFn: ({ id, patch }) => parametresApi.updateUser(id, patch), onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }), onError: (e) => toast.error(e.response?.data?.detail || 'Échec') });
  const del = useMutation({
    mutationFn: (id) => parametresApi.deleteUser(id),
    onSuccess: () => { toast.success('Utilisateur supprimé'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Suppression impossible'),
  });
  const resend = useMutation({
    mutationFn: (id) => parametresApi.resendCredentials(id),
    onSuccess: (d) => toast.success(d.email_sent ? 'Identifiants renvoyés par email' : `Nouveau mot de passe temporaire : ${d.temp_password}`),
    onError: (e) => toast.error(e.response?.data?.detail || 'Échec du renvoi'),
  });

  const remove = (u) => { if (window.confirm(`Supprimer définitivement l'utilisateur « ${u.name || u.email} » ? Cette action est irréversible.`)) del.mutate(u.id); };

  // Non-gérant : auto-gestion de son seul compte.
  if (!isGerant) return <SelfProfile />;

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-[900px]">
      <h2 className="font-title text-3xl mb-5">Utilisateurs et rôles</h2>
      <Card className="p-4 mb-5" data-testid="invite-form">
        <p className="font-title text-lg mb-3">Inviter un utilisateur</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <input className="vd-input h-9" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" />
          <input className="vd-input h-9" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
          <select className="vd-input h-9" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
            {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <Button variant="primary" className="mt-3" onClick={() => invite.mutate()} disabled={!form.email || invite.isPending} data-testid="invite-btn">
          <UserPlus className="w-4 h-4" /> {invite.isPending ? 'Envoi…' : 'Inviter'}
        </Button>
      </Card>
      <div className="vd-card overflow-hidden">
        <table className="w-full text-[13.5px]" data-testid="users-table">
          <thead><tr className="text-left kicker"><th className="py-2.5 px-3">Nom</th><th className="py-2.5 px-3">Email</th><th className="py-2.5 px-3">Rôle</th><th className="py-2.5 px-3">État</th><th className="py-2.5 px-3 text-right">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="hair-t" data-testid={`user-${u.id}`}>
                <td className="py-2.5 px-3">{u.name}</td>
                <td className="py-2.5 px-3 text-n600">{u.email}</td>
                <td className="py-2.5 px-3">
                  <select className="vd-input h-8 text-[12px]" value={u.role} onChange={(e) => upd.mutate({ id: u.id, patch: { role: e.target.value } })}>
                    {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="py-2.5 px-3">
                  <button onClick={() => upd.mutate({ id: u.id, patch: { active: !u.active } })} data-testid={`toggle-${u.id}`}>
                    <Tag tone={u.active ? 'fill' : 'neutral'}>{u.active ? 'Actif' : 'Désactivé'}</Tag>
                  </button>
                </td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditing(u)} title="Modifier" className="p-1.5 text-n600 hover:text-ink" data-testid={`user-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => resend.mutate(u.id)} title="Renvoyer les identifiants par email" className="p-1.5 text-n600 hover:text-gold700" data-testid={`user-resend-${u.id}`}><Mail className="w-4 h-4" /></button>
                    <button onClick={() => remove(u)} title="Supprimer l'utilisateur" className="p-1.5 text-n600 hover:text-red-700" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <UserEditModal user={editing} roles={ROLES} onClose={() => setEditing(null)}
        onSave={(patch) => upd.mutate({ id: editing.id, patch }, { onSuccess: () => { toast.success(patch.password ? 'Utilisateur mis à jour — email de notification envoyé' : 'Utilisateur mis à jour'); setEditing(null); } })}
        onResend={() => resend.mutate(editing.id)} />}
    </div>
  );
}

function UserEditModal({ user, roles, onClose, onSave, onResend }) {
  const [form, setForm] = useState({ name: user.name || '', email: user.email || '', role: user.role, password: '', password2: '' });
  const save = () => {
    const pw = form.password.trim();
    if (pw && pw !== form.password2.trim()) { toast.error('Les deux mots de passe ne correspondent pas'); return; }
    if (pw && pw.length < 6) { toast.error('Mot de passe trop court (6 caractères minimum)'); return; }
    const patch = { name: form.name, email: form.email, role: form.role };
    if (pw) patch.password = pw;
    onSave(patch);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div className="bg-paper border border-hair rounded-lg shadow-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="user-edit-modal">
        <h3 className="font-title text-2xl mb-4">Modifier l'utilisateur</h3>
        <div className="space-y-3">
          <label className="block"><span className="text-[12px] text-n600">Nom</span>
            <input className="vd-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-edit-name" /></label>
          <label className="block"><span className="text-[12px] text-n600">Email</span>
            <input className="vd-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-edit-email" /></label>
          <label className="block"><span className="text-[12px] text-n600">Rôle</span>
            <select className="vd-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-edit-role">
              {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label className="block"><span className="text-[12px] text-n600">Nouveau mot de passe (laisser vide pour ne pas changer)</span>
            <input type="password" className="vd-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••" data-testid="user-edit-password" /></label>
          {form.password.trim() && (
            <label className="block"><span className="text-[12px] text-n600">Vérifier le nouveau mot de passe</span>
              <input type="password" className="vd-input" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} placeholder="••••••" data-testid="user-edit-password2" /></label>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-5">
          <Button onClick={onResend} data-testid="user-edit-resend"><Mail className="w-4 h-4" /> Renvoyer les identifiants</Button>
          <div className="flex gap-2">
            <Button onClick={onClose}>Annuler</Button>
            <Button variant="primary" disabled={!form.email.trim()} onClick={save} data-testid="user-edit-save">Enregistrer</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
